/**
 * Centralized chart color palette system.
 *
 * Four palette kinds:
 * - sequentialForest: ordinal data along one dimension (years, intensity)
 * - role: series with functional meaning (focal, reference, envelope)
 * - categorical: nominal categories (12 brand-tuned stops)
 * - semanticOverrides: locked field/value mappings (status, technology, etc.)
 *
 * Pick by data type, not chart type.
 */

export const PALETTE = {
  sequentialForest: [
    "#0D2218", // Most committed / current focus
    "#1F4D33", // Recent
    "#3A7D3F", // Mid
    "#7BA37E", // Older
    "#B8D0BF", // Oldest / faintest
  ],

  categorical: [
    "#1F4D33", // forest-500
    "#C08E3A", // ochre-500
    "#3A7D3F", // moss-500
    "#D9BC78", // ochre-300
    "#0D2218", // forest-700
    "#7BA37E", // moss-300
    "#8C5F0A", // ochre-600
    "#56615B", // gray-700
    "#B8D0BF", // forest-200
    "#E6D4A8", // ochre-200
    "#43504A", // gray-800
    "#ADB3AF", // gray-400
  ],

  role: {
    focal: "#1F4D33",
    focalSoft: "#6A8A73",
    reference: "#8D948F",
    envelope: "#ADB3AF",
    envelopeHi: "#6A8A73",
    envelopeLo: "#C08E3A",
    priceUp: "#1F4D33",
    priceDown: "#C08E3A",
    priceNeutral: "#ADB3AF",
    priceNoData: "#E2E4E2",
  },

  semanticOverrides: {
    status: {
      Available: "#B8D0BF",
      Pending: "#D9BC78",
      Sold: "#1F4D33",
      Reserved: "#0D2218",
      "Non-transferable": "#56615B",
      "Own Use": "#8D948F",
    } as Record<string, string>,

    portfolioType: {
      "Self-managed": "#ADB3AF",
      "Spot Sales": "#C08E3A",
      "Waiting For Action": "#D9BC78",
      PPA: "#0D2218",
      Forward: "#1F4D33",
      "Own Use": "#8D948F",
    } as Record<string, string>,

    technology: {
      Solar: "#C08E3A",
      Wind: "#6A8A73",
      Hydro: "#1F4D33",
      Marine: "#B8D0BF",
      Biomass: "#8C5F0A",
      Geothermal: "#56615B",
      Nuclear: "#43504A",
    } as Record<string, string>,

    // Keyed by the raw stacked-chart values (String(production_support)): "null" means the
    // registry gave us no support data, rendered in a washed-out gray to read as "missing".
    production_support: {
      true: "#1F4D33",
      false: "#ADB3AF",
      null: "#E2E4E2",
    } as Record<string, string>,

    certificateStatus: {
      Expired: "#E6D4A8",
      Sold: "#1F4D33",
      "Awaiting Sale": "#B8D0BF",
    } as Record<string, string>,
  } as Record<string, Record<string, string>>,
} as const;

export interface ChartColorOpts {
  field?: string;
  value?: string;
  index?: number;
}

/**
 * Resolves a chart color from the palette system.
 *
 * Priority:
 * 1. Explicit semantic override (field + value match)
 * 2. Categorical palette by index
 * 3. LCh-generated extension for index >= 12 (dev-mode warns)
 */
export function getChartColor(opts: ChartColorOpts): string {
  if (opts.field && opts.value) {
    const fieldOverrides = PALETTE.semanticOverrides[opts.field];
    if (fieldOverrides) {
      const hit = fieldOverrides[opts.value];
      if (hit) return hit;
    }
  }

  const i = opts.index ?? 0;

  if (i < 0) {
    return PALETTE.categorical[0];
  }

  if (i < PALETTE.categorical.length) {
    return PALETTE.categorical[i];
  }

  return generateExtended(i);
}

/**
 * Brand-tuned extension for indices beyond the 12-stop categorical palette.
 * Uses LCh color space with locked L* and C* so generated colors share
 * luminance/chroma with the curated set.
 */
function generateExtended(index: number): string {
  const goldenRatio = 0.618033988749895;
  const hue = ((index * goldenRatio) % 1) * 360;
  const L = 50;
  const C = 28;
  return lchToHex(L, C, hue);
}

/**
 * Convert CIE LCh (L*~0-100, C*~0-150, h deg 0-360) to a sRGB hex string.
 * Uses D65 white point. Out-of-gamut colors are clamped.
 */
export function lchToHex(L: number, C: number, h: number): string {
  const hRad = (h * Math.PI) / 180;
  const a = Math.cos(hRad) * C;
  const b = Math.sin(hRad) * C;

  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;
  const eps = 216 / 24389;
  const kappa = 24389 / 27;
  const fxCubed = fx * fx * fx;
  const fzCubed = fz * fz * fz;
  const xr = fxCubed > eps ? fxCubed : (116 * fx - 16) / kappa;
  const yr = L > kappa * eps ? Math.pow((L + 16) / 116, 3) : L / kappa;
  const zr = fzCubed > eps ? fzCubed : (116 * fz - 16) / kappa;

  const X = xr * 0.95047;
  const Y = yr * 1.0;
  const Z = zr * 1.08883;

  const rLin = 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z;
  const gLin = -0.969266 * X + 1.8760108 * Y + 0.041556 * Z;
  const bLin = 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z;

  const toSrgb = (v: number) => {
    const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.round(Math.min(1, Math.max(0, c)) * 255);
  };

  const r = toSrgb(rLin);
  const g = toSrgb(gLin);
  const bb = toSrgb(bLin);
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(bb)}`.toUpperCase();
}
